import { useGetPlaybookQuery } from '@carboncopyco/data';
import { PlayIcon } from '@carboncopyco/icons';
import type { Play } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Container,
  SkeletonRowLoading,
  TextBase,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type Col, FixedTable } from '../../../FixedTable';
import { GiveADemoFlowModal } from '../../../PresentationModals';
import { Screenshot } from '../../../Screenshot';
import { useFilesServiceContext } from '../../../utils';
import { CreatedOnTooltip } from '../../CreatedOnTooltip';
import { EmptyState } from '../../EmptyState';
import { TableRowWrapper } from '../../TableRowWrapper';
import { handleSinglePlural } from '../../utils';
import { usePlaybookPageProps } from '../PlaybookPageContext';
import { usePresentationsContext } from '../PresentationsContext';
import { Controls } from './Controls';
import { RowButtons } from './RowButtons';

export const cols: Col<Play>[] = [
  {
    alignment: 'left',
    title: 'Opportunity',
    width: 220,
    maxContentWidth: 200,
    render: (presentation) => {
      return (
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="w-6 h-6 shrink-0">
            <Screenshot
              customElement={
                !presentation.image && (
                  <div className="flex items-center justify-center h-full bg-ds-primary-100">
                    <PlayIcon color={ColorHex.primary300} />
                  </div>
                )
              }
              image={presentation.image}
              alt="playbook image"
              rounded="full"
            />
          </div>
          <div className="flex flex-col flex-grow overflow-hidden">
            <TooltipBubble
              disabled={presentation.forClient.length < 18}
              placement="top-start"
              title={presentation.forClient}
            >
              <div className="overflow-hidden truncate text-ds-natural-600">
                <TextBase
                  ellipsis
                  color="text-ds-natural-600"
                  label={presentation.forClient}
                />
              </div>
            </TooltipBubble>
          </div>
        </div>
      );
    },
  },
  {
    title: 'Date',
    alignment: 'left',
    width: 120,
    maxContentWidth: 116,
    render: ({ createTime }) => <CreatedOnTooltip dateTime={createTime} />,
  },
  {
    alignment: 'left',
    title: 'Creator',
    width: 150,
    maxContentWidth: 130,
    render: ({ creator }) => (
      <div className="flex items-center">
        <UserBasicInfo
          size="small"
          user={creator}
          textColor="text-ds-natural-600"
          truncateWidth={110}
          tooltip={creator.name.length > 15}
        />
      </div>
    ),
  },
  {
    alignment: 'right',
    title: 'Presented',
    width: 85,
    render: ({ totalPresentationViews }) => (
      <TooltipBubble
        title={handleSinglePlural(totalPresentationViews, 'Presented')}
        placement="top"
      >
        <TextBase
          label={totalPresentationViews || '-'}
          color="text-ds-natural-600"
        />
      </TooltipBubble>
    ),
  },
  {
    alignment: 'right',
    title: '',
    width: 375,
    render: (presentation) => <RowButtons presentation={presentation} />,
  },
];

export const PresentationsTable = () => {
  const {
    rootPath,
    organizationId,
    playbookId,
    playPresentation,
    workspaceId,
  } = usePlaybookPageProps();
  const { filesServiceDomain } = useFilesServiceContext();

  const [
    showGiveADemoModal,
    {
      setTrue: setTrueShowGiveADemoModal,
      setFalse: setFalseShowGiveADemoModal,
    },
  ] = useBoolean();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    fetchArgs,
    fetchFirstBatch,
    fetchNextBatch,
    isLoadingBatch,
    isEmpty,
    error,
    isLoading,
    presentations,
    addItem,
  } = usePresentationsContext();

  useEffect(() => {
    const newPlay = location.state?.play;
    if (newPlay) {
      addItem(newPlay);
    }
  }, [location]);

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchFirstBatch, fetchArgs]);

  const { data: playbook } = useGetPlaybookQuery({
    organizationId,
    workspaceId,
    id: playbookId,
  });

  const handleOnPresent = async (play: Play) => {
    await playPresentation(play);
    setFalseShowGiveADemoModal();
  };

  if (isEmpty) {
    return (
      <>
        <EmptyState
          title="Demo has no active presentations"
          description="Once a demo presentation is created, you can track it from here"
          action={
            <Button
              link
              blue
              label="Give a demo"
              leftIcon={<PlayIcon />}
              onClick={setTrueShowGiveADemoModal}
            />
          }
        />
        {showGiveADemoModal && (
          <GiveADemoFlowModal
            workspaceId={workspaceId}
            open={showGiveADemoModal}
            asset={{
              name: playbook.name,
              id: playbook.id,
              type: 'playbook',
              assets: playbook.assets,
            }}
            onClose={setFalseShowGiveADemoModal}
            onPresent={handleOnPresent}
            onSave={() => {
              navigate(`${rootPath}/presentations`);
              setFalseShowGiveADemoModal();
            }}
            filesServiceUrl={filesServiceDomain}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Controls />
      <div className="py-5">
        <FixedTable<Play>
          error={error}
          data={presentations ?? []}
          cols={cols}
          isLoading={isLoading}
          handleFetchNextBatch={() => fetchNextBatch(fetchArgs)}
          isLoadingBatch={isLoadingBatch}
          sideSpacing
          infiniteListMaxHeight={600}
          rowWrapper={TableRowWrapper}
          loader={
            <Container>
              <div className="w-ds-lg-modal">
                <SkeletonRowLoading
                  medium
                  itemsCount={5}
                />
              </div>
            </Container>
          }
        />
      </div>
    </>
  );
};
